import {
  ContentfulBlocks,
  PageBlockSpacingContainer,
} from '@dreamstack/accentro-contentful'
import type { GetStaticPropsPlusOptionsGetProps } from '@dreamstack/artemis'
import {
  OtherLocaleUrlsContextProvider,
  allLocales,
  useLocale,
  useOtherLocale,
  useTranslation,
} from '@dreamstack/i18n'
import {
  queryGetNewsArticlePreviews,
  useGetNewsArticlePreviewsQuery,
} from '@dreamstack/investors-graphql'
import {
  NewsSearchResultBox,
  SearchAutocomplete,
  useHasSearchContext,
  useNewsSearchHitsAsArticles,
  useSearchIsSearching,
} from '@dreamstack/search'
import { DreamstackSeo } from '@dreamstack/seo'
import { trpc } from '@dreamstack/trpc/client'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import filter from 'lodash/filter'
import flatMap from 'lodash/flatMap'
import head from 'lodash/head'
import map from 'lodash/map'
import tail from 'lodash/tail'
import trim from 'lodash/trim'
import type { FunctionComponent } from 'react'
import { useMemo, useState } from 'react'
import tw, { styled } from 'twin.macro'
import type { HeroProps } from '../index'
import {
  HeadingH4,
  IS_ACCENTRO_WEB,
  MainLayout,
  NewsHeader,
  NewsTypes,
  useGetNewsTypeUrlPartReverse,
} from '../index'
import { ArticlePreviewGrid } from './ArticlePreviewGrid'
import { FirstArticle } from './FirstArticle'
import { LoadMoreNews } from './LoadMoreNews'
import {
  newsHubOtherLocaleUrls as baseNewsHubOtherLocaleUrls,
  getNewsPath,
  getNewsTypeUrlPart,
  getNewsTypeUrlPartReverse,
  newsBaseUrls,
} from './getNewsPath'
import type { NewsType } from './newsTypeUrls.static'

const FIRST_ARTICLE_BIG = IS_ACCENTRO_WEB

const NewsTypesAndSearchWrapper = tw.div`flex flex-col space-y-px-32 md:(flex-row space-y-0 mr-px-16)`

const NewsTypesWrapper = styled.div<{ isClosed: boolean }>`
  ${({ isClosed }) => isClosed && tw`hidden`}
`

const FilterArticlesButtonWrapper = styled.div`
  ${tw`md:hidden lg:hidden`}
`

export type NewsHubProps = {
  showSubhero?: boolean
  newsTypes: NewsType[]
}

export const NewsHub: FunctionComponent<
  React.PropsWithChildren<NewsHubProps>
> = ({ showSubhero = true, newsTypes }) => {
  const [isClosed, setIsClosed] = useState(true)
  const newsType = useGetNewsTypeUrlPartReverse()
  const { data: articles } = useGetNewsArticlePreviewsQuery({
    variables: { newsType },
  })
  const newsArticles = articles?.newsArticles?.items

  const { firstArticle, otherArticles, length } = useMemo(() => {
    const filteredNewsArticles = filter(
      newsArticles,
      (f) => !newsType || f?.newsType === newsType
    )
    const firstArticle = FIRST_ARTICLE_BIG ? head(filteredNewsArticles) : null
    const otherArticles = FIRST_ARTICLE_BIG
      ? tail(filteredNewsArticles)
      : filteredNewsArticles
    const length = filteredNewsArticles?.length || 0
    return { firstArticle, otherArticles, length }
  }, [newsArticles, newsType])

  const topSixNewsSearchArticles = filter(
    newsArticles,
    (f) => !newsType || f?.newsType === newsType
  ).slice(0, 6)

  const t = useTranslation()

  const subHero = showSubhero ? <NewsHeader /> : undefined
  // STATIC PAGE STUFF
  const locale = useLocale()
  const slug = trim(newsBaseUrls[locale], '/')

    const { data } = trpc.cms.getStaticPage.useQuery({ locale, slug }, {
        trpc: { ssr: true },
        staleTime: Infinity,
    })

  const { data: nestedNewsPage } = trpc.cms.getStaticPageByNewstype.useQuery(
    { locale, newsType: newsType ?? '' },
      {
          trpc: { ssr: true },
          staleTime: Infinity,
          enabled: !!newsType && IS_ACCENTRO_WEB,
      }
  )
  const hero = data?.page?.hero

  const filterNewsButtonsHandleClick = () => {
    setIsClosed(!isClosed)
  }

  // SEARCH
  const isSearching = useSearchIsSearching()
  const hasSearch = useHasSearchContext()
  const newsSearchArticles = useNewsSearchHitsAsArticles()

  // OTHER LOCALE URLs WITH NEWSTYPE
  // copy base urls which may get overwritten
  const newsHubOtherLocaleUrls = { ...baseNewsHubOtherLocaleUrls }

  const otherLocale = useOtherLocale()

  // if a newstype is selected and other locale exists:
  if (!!newsType && otherLocale) {
    //get the newspath translated for the newstype
    const hrefOtherLocale = getNewsPath({
      locale: otherLocale,
      newsType: newsType as NewsType,
      slug: '',
    })

    //overwrite the url of the other locale
    newsHubOtherLocaleUrls[otherLocale] = {
      url: hrefOtherLocale,
    }
  }

  const pillarPageTitle = nestedNewsPage?.page?.seo?.title
  const title = !!pillarPageTitle
    ? pillarPageTitle
    : t('accentro:newsHub.title')

  return (
    <OtherLocaleUrlsContextProvider context={newsHubOtherLocaleUrls}>
      <DreamstackSeo title={title} />
      <MainLayout
        hero={
          hero
            ? ({ ...hero, noMarginBottom: true } as HeroProps)
            : {
                title,
                forceDarkBg: true,
                size: 'small',
                noMarginBottom: true,
              }
        }
        subHero={subHero}
      >
        <PageBlockSpacingContainer>
          <NewsTypesAndSearchWrapper>
            {isSearching ? (
              <NewsSearchResultBox />
            ) : (
              <>
                <FilterArticlesButtonWrapper>
                  <HeadingH4>
                    <a
                      onClick={filterNewsButtonsHandleClick}
                      tw="cursor-pointer text-accentroAqua-full text-lg"
                      onKeyPress={filterNewsButtonsHandleClick}
                      role="button"
                      tabIndex={0}
                    >
                      {t('accentro:newsHub.filterLabel')}
                      {!isClosed ? (
                        <ExpandLessIcon fontSize="large" />
                      ) : (
                        <ExpandMoreIcon fontSize="large" />
                      )}
                    </a>
                  </HeadingH4>
                </FilterArticlesButtonWrapper>
                <NewsTypesWrapper isClosed={isClosed} tw="md:block">
                  <NewsTypes newsTypes={newsTypes} />
                </NewsTypesWrapper>
              </>
            )}
            {hasSearch && (
              <div tw="md:min-width[380px]">
                <SearchAutocomplete indexType="news" />
              </div>
            )}
          </NewsTypesAndSearchWrapper>
          {!length && (
            <HeadingH4 tw="text-center my-px-64">
              {t('accentro:newsHub.noArticles')}
            </HeadingH4>
          )}
          {!isSearching && !nestedNewsPage && firstArticle && (
            <FirstArticle article={firstArticle} />
          )}
          {!isSearching && nestedNewsPage && (
            <PageBlockSpacingContainer>
              <ArticlePreviewGrid articles={topSixNewsSearchArticles} />
              <ContentfulBlocks
                customProps={{
                  newsType: nestedNewsPage?.page?.newsType,
                  region: nestedNewsPage?.page?.region,
                }}
                blocks={nestedNewsPage?.blocksData}
              />
            </PageBlockSpacingContainer>
          )}

          <LoadMoreNews
            articles={isSearching ? newsSearchArticles : otherArticles}
          />
        </PageBlockSpacingContainer>
      </MainLayout>
    </OtherLocaleUrlsContextProvider>
  )
}

export const getStaticNewsProps: GetStaticPropsPlusOptionsGetProps = async ({
  apolloClient,
  params,
  locale,
}) => {
  if (!locale) {
    return {
      notFound: true,
    }
  }
  const newsTypeUrl = params ? (params.newsTypeUrl as string) : undefined
  const newsType = newsTypeUrl
    ? getNewsTypeUrlPartReverse({
        locale: locale as any,
        newsTypeUrl,
      })
    : undefined

  await queryGetNewsArticlePreviews({ apolloClient, variables: { newsType } })
}

export const getStaticNewsTypePaths = (newsTypes: NewsType[]) => {
  return flatMap(allLocales, (locale) => {
    return map(newsTypes, (newsType) => {
      const newsTypeUrl = getNewsTypeUrlPart({ locale, newsType })
      return {
        params: {
          newsTypeUrl,
        },
        locale,
      }
    })
  })
}
